import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MentionUser } from '../../../../shared/directives/mention-input.directive';

@Component({
  selector:        'app-mention-dropdown',
  standalone:      true,
  imports:         [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isOpen && users.length > 0) {
      <div #panel
           class="absolute left-0 right-0 z-50 bg-white rounded-xl border border-gray-200
                  shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark
                  overflow-hidden"
           [ngClass]="position === 'below' ? 'top-full mt-1' : 'bottom-full mb-1'">

        <!-- Header -->
        <div class="px-3 py-2 text-[11px] font-semibold text-gray-400
                    uppercase tracking-wide border-b border-gray-100
                    dark:border-gray-800 flex items-center gap-1.5">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2.5"
               stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="8" r="4"/>
            <path d="M20 21a8 8 0 1 0-16 0"/>
          </svg>
          Mention someone
        </div>

        <!-- User list -->
        <div class="max-h-52 overflow-y-auto">
          <button *ngFor="let user of users; trackBy: trackById"
                  type="button"
                  (mousedown)="pick(user)"
                  class="flex w-full items-center gap-3 px-3 py-2.5
                         hover:bg-[#fff5f7] dark:hover:bg-gray-800
                         transition-colors text-left group">

            <!-- Avatar -->
            <div class="relative flex-shrink-0">
              <img *ngIf="user.profileImage?.urlSmall"
                   [src]="user.profileImage!.urlSmall"
                   [alt]="user.displayName"
                   class="w-9 h-9 rounded-full object-cover
                          border border-gray-100 dark:border-gray-700" />

              <!-- Fallback initial avatar -->
              <div *ngIf="!user.profileImage?.urlSmall"
                   class="w-9 h-9 rounded-full bg-[#FFD4E0] flex items-center
                          justify-center text-[#D31148] font-bold text-sm flex-shrink-0">
                {{ user.displayName.charAt(0).toUpperCase() }}
              </div>

              <!-- Green dot = friend -->
              <span *ngIf="user.isFriend"
                    class="absolute -bottom-0.5 -right-0.5 w-3 h-3
                           bg-[#10b981] rounded-full border-2 border-white
                           dark:border-gray-dark">
              </span>
            </div>

            <!-- Names -->
            <div class="flex-1 min-w-0">
              <div class="text-sm font-semibold text-gray-800 dark:text-gray-100
                          truncate group-hover:text-[#D31148] transition-colors">
                {{ user.displayName }}
              </div>
              <div class="text-xs text-gray-400 truncate">@{{ user.userName }}</div>
            </div>

            <!-- Friend badge -->
            <span *ngIf="user.isFriend"
                  class="flex-shrink-0 text-[10px] font-semibold text-[#10b981]
                         bg-emerald-50 border border-emerald-200 rounded-full
                         px-2 py-0.5 dark:bg-emerald-900/30 dark:border-emerald-800">
              Friend
            </span>

          </button>
        </div>

      </div>
    }
  `,
})
export class MentionDropdownComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() users: MentionUser[] = [];
  @Input() position: 'above' | 'below' = 'above';
  @Output() selected = new EventEmitter<MentionUser>();

  @ViewChild('panel') private panelRef?: ElementRef<HTMLElement>;

  private wasVisible = false;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  ngOnChanges(): void {
    // `isOpen` flips before the search results land, so the panel only actually
    // renders once both are true — that transition is what we react to.
    const visible = this.isOpen && this.users.length > 0;
    if (visible && !this.wasVisible) this.revealPanel();
    this.wasVisible = visible;
  }

  /**
   * Scrolls the panel into view when it opens.
   *
   * The dropdown is anchored under its input, which in tall hosts — the
   * create-post background caption above all — sits far enough down the modal
   * that the list renders below the fold. Users typed `@a`, saw nothing appear,
   * and had no reason to scroll looking for it.
   *
   * `block: 'nearest'` scrolls the minimum needed and does nothing at all when
   * the panel is already fully visible, so the many places this dropdown
   * already worked are left alone.
   */
  private revealPanel(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // Defer past the render that the @if above is about to perform.
    requestAnimationFrame(() => {
      this.panelRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  pick(user: MentionUser): void {
    this.selected.emit(user);
  }

  trackById(_: number, u: MentionUser): string {
    return u._id;
  }
}
